import defaultConfig from "../schema/defaultDoctorConsole.config.json";
import fieldSetsSchema from "../schema/doctorConsoleFieldSets.json";
import { evaluateRule, buildRuleContext } from "./ruleEngine";
import { resolveSectionIcon } from "../registry/sectionIcons";
import { applySectionConstraints } from "./applySystemFieldConstraints";

const sortByOrder = (items = []) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const mergeFeatures = (baseFeatures = {}, overrideFeatures = {}) => {
  const merged = { ...baseFeatures };
  Object.entries(overrideFeatures ?? {}).forEach(([id, override]) => {
    merged[id] = { ...baseFeatures[id], ...override };
  });
  return merged;
};

/**
 * Merges a canonical field/column list with a saved override list by identity.
 *
 * The override wins for entries it already knows about (enabled, order, label),
 * but canonical entries it predates — fields shipped after that config was
 * saved — are appended instead of being frozen out of the console and Manager.
 */
const mergeListByIdentity = (canonicalList = [], overrideList, identity) => {
  if (!Array.isArray(overrideList)) return canonicalList;

  const canonicalByKey = new Map(
    canonicalList.map((item) => [identity(item), item]),
  );
  const seen = new Set();

  const fromOverride = overrideList.map((item) => {
    const key = identity(item);
    seen.add(key);
    return canonicalByKey.has(key)
      ? { ...canonicalByKey.get(key), ...item }
      : item;
  });

  return [
    ...fromOverride,
    ...canonicalList.filter((item) => !seen.has(identity(item))),
  ];
};

const mergeFieldSets = (canonicalFieldSets = {}, overrideFieldSets) => {
  if (!overrideFieldSets) return canonicalFieldSets;

  const merged = { ...canonicalFieldSets };
  Object.entries(overrideFieldSets).forEach(([id, overrideSet]) => {
    const canonicalSet = canonicalFieldSets[id];
    if (!canonicalSet) {
      merged[id] = overrideSet;
      return;
    }
    merged[id] = {
      ...canonicalSet,
      ...overrideSet,
      fields: mergeListByIdentity(
        canonicalSet.fields,
        overrideSet.fields,
        (field) => field.id ?? field.key,
      ),
      tableColumns: mergeListByIdentity(
        canonicalSet.tableColumns,
        overrideSet.tableColumns,
        (column) => column.key,
      ),
    };
  });
  return merged;
};

const mergeConfig = (base, override = null) => {
  const merged = {
    ...base,
    enums: { ...fieldSetsSchema.enums, ...base.enums, ...override?.enums },
    fieldSets: mergeFieldSets(
      { ...fieldSetsSchema.fieldSets, ...base.fieldSets },
      override?.fieldSets,
    ),
  };
  if (!override) {
    return {
      ...merged,
      sections: applySectionConstraints(merged.sections),
    };
  }
  return {
    ...merged,
    ...override,
    enums: { ...merged.enums, ...override.enums },
    fieldSets: merged.fieldSets,
    features: mergeFeatures(base.features, override.features),
    sections: applySectionConstraints(merged.sections, override.sections),
    dependencies: override.dependencies ?? merged.dependencies,
  };
};

export const loadDoctorConsoleConfig = (override = null) =>
  mergeConfig(defaultConfig, override);
export const getSectionByKey = (config, key) =>
  (config.sections || []).find((s) => s.key === key);

export const getFeatureConfig = (config, featureId) =>
  config?.features?.[featureId] ?? null;

export const isFeatureEnabled = (config, featureId, ruleContext) => {
  const feature = getFeatureConfig(config, featureId);
  if (!feature || feature.enabled === false) return false;
  if (feature.visible) {
    return evaluateRule(feature.visible, ruleContext);
  }
  return true;
};

export const getVisibleSections = (config, ruleContext) =>
  sortByOrder(config.sections || [])
    .filter((section) => section.enabled !== false)
    .filter((section) => evaluateRule(section.visible ?? { when: "always" }, ruleContext))
    .map((section) => ({
      name: section.key,
      displayName: section.label ?? section.key,
      icon: resolveSectionIcon(section.icon),
      config: section,
    }));

export const getVisibleHistorySubSections = (historySection, ruleContext) => {
  if (!historySection?.subSections) return [];
  return sortByOrder(historySection.subSections).filter((sub) =>
    evaluateRule(sub.visible ?? { when: "always" }, ruleContext),
  );
};

export const buildMedicalBackgroundInitial = (config) => {
  const historySection = (config.sections || []).find((s) => s.key === "History");
  const initial = {};
  (historySection?.subSections || []).forEach((sub) => {
    if (sub.dataStore === "medicalBackground" && sub.dataPath) {
      initial[sub.dataPath] = { prev: [], new: [] };
    }
  });
  return initial;
};

export const getDefaultActiveSectionKey = (config, ruleContext = buildRuleContext()) => {
  const visible = getVisibleSections(config, ruleContext);
  return visible[0]?.name ?? "Chief Complaints";
};

export { buildRuleContext, evaluateRule };
